import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from 'context/AuthProvider';
import { Spade, Mail, KeyRound, LogIn, AlertCircle, Loader2 } from 'lucide-react';

const Login = () => {
  const { login, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Already logged in — send them where they were headed (or the dashboard).
  if (token) {
    const dest = (location.state as any)?.from?.pathname || '/dashboard';
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);
    if (result.ok) {
      const dest = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(dest, { replace: true });
    } else {
      setError(result.message || 'Login failed.');
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-app">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-dark via-navy-900 to-slate-950 p-12 text-white">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-brand-gold/5 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-brand-gold text-brand-dark shadow-lg">
            <Spade size={22} fill="currentColor" />
          </div>
          <span className="text-lg font-extrabold tracking-tight">Teen Patti Admin</span>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-3xl font-extrabold leading-snug">
            Run the table.<br />
            <span className="text-brand-gold">Command every hand.</span>
          </h2>
          <p className="mt-4 text-sm text-faint leading-relaxed">
            Monitor live battles, manage players and wallets, and configure the game — all from one
            secure console.
          </p>
        </div>

        <div className="relative text-xs text-muted">
          &copy; {new Date().getFullYear()} Teen Patti. Authorized personnel only.
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-dark text-brand-gold mb-4 shadow-sm">
              <Spade size={28} fill="currentColor" />
            </div>
            <h1 className="text-2xl font-extrabold text-heading">Teen Patti Admin</h1>
          </div>

          <div className="bg-card rounded-2xl shadow-card-lg border border-line p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-extrabold text-heading">Welcome back</h1>
              <p className="text-sm text-muted mt-1">Sign in to access the administration console.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-brand-danger/10 text-brand-danger border border-brand-danger/20 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 text-faint" size={18} />
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-line pl-11 pr-4 py-3 shadow-sm focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:text-sm outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Password</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-3.5 text-faint" size={18} />
                  <input
                    type="password"
                    required
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-line pl-11 pr-4 py-3 shadow-sm focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:text-sm outline-none transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand-gold hover:bg-gold-500 text-brand-dark font-bold rounded-xl transition-all shadow-sm disabled:opacity-60"
              >
                {submitting ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : <><LogIn size={18} /> Sign In</>}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-faint mt-6">
            Authorized personnel only. All activity is audited.
            <br />
            <a href="/consumer/login" className="text-brand-gold hover:underline mt-2 inline-block">
              Consumer portal login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;